import { ref, computed, onMounted, type Ref, type ComputedRef } from "vue";
import { isRoleOrInRoles } from "@/api/account";
import { Role } from "@/enums/Role";
import { logError, logInfo } from "@/helpers/logError";
import api from "@/api";

type MeAccountResponse = { account?: { roles?: Array<{ name?: string }> } };

export interface UseCurrentUserRole {
	me: Ref<MeAccountResponse | null>;
	roleName: ComputedRef<string | undefined>;
	canManageSparks: ComputedRef<boolean>;
	canManageUsers: ComputedRef<boolean>;
	canManageGuidedInstructions: ComputedRef<boolean>;
	canManageTags: ComputedRef<boolean>;
	canManageGoogleReviewSettings: ComputedRef<boolean>;
	isLoading: Ref<boolean>;
}

/**
 * Per-component fetch of the current user's role for gating admin-only CTAs.
 * The route guard already enforces access on navigation; this exists to avoid
 * rendering links to destinations the user cannot open.
 *
 * Concurrency / freshness: defers to `api.getMe()`, which already caches the
 * resolved response in `this._me`, deduplicates concurrent calls via
 * `_mePromise`, and is invalidated by `api.invalidateMe()` / `api.refreshMe()`.
 * That means two consumers on the same page share one HTTP request, and an
 * organization switch (which calls `api.refreshMe()` in App.vue) correctly
 * invalidates the cached role for the NEW org's data — without us re-rolling
 * a parallel cache here that would go stale on org switch.
 *
 * Callers should gate the rendered element on `!isLoading.value && canX.value`
 * (or render a skeleton while loading) to avoid a hide-then-appear flash for
 * admins on the first paint.
 */
export function useCurrentUserRole(): UseCurrentUserRole {
	const me = ref<MeAccountResponse | null>(null);
	const isLoading = ref<boolean>(true);

	const roleName = computed<string | undefined>(() => me.value?.account?.roles?.[0]?.name);

	// The admin role set the admin-only routes require (see router/index.js
	// `requiredRoles`). Both gates below mirror this same route contract today
	// but stay separate named computeds so a future divergence touches one only.
	const adminRoles = [Role.Administrator, Role.SuperAdministrator, Role.Support];

	// Admin detection must consider EVERY role on the account, not just the first
	// one. The route guard admits a user if ANY of their roles is admin
	// (`requiredRoles.some(r => account.hasRole(r))`), and App.vue's own admin
	// check does the same (`roles.map(name).some(isRoleOrInRoles)`). Reading only
	// `roles[0]` falsely locked out an admin whose admin role was not listed first
	// even though the route guard had already let them in (LSP-16036). This matches
	// the route guard's any-slot semantics; the per-name match is intentionally
	// case-insensitive (isRoleOrInRoles) — slightly more lenient than the route's
	// case-sensitive hasRole, which only ever widens the UI gate, never the server
	// save authority.
	const hasAdminRole = computed<boolean>(() => (me.value?.account?.roles ?? []).some((role) => isRoleOrInRoles(role?.name, adminRoles)));

	const canManageSparks = computed<boolean>(() => hasAdminRole.value);

	// Gates user-management CTAs — e.g. the edit-user icon on UserAutoComplete —
	// so the User role cannot open settings it can't save.
	const canManageUsers = computed<boolean>(() => hasAdminRole.value);

	// Mirrors the instruction template route gate so non-admins cannot compose
	// Guided Instructions only to be rejected by the save API.
	const canManageGuidedInstructions = computed<boolean>(() => hasAdminRole.value);

	// LSP-20406: gates tag-create affordances (POST /labels is admin-only — LSP-15564), the same
	// isAdmin gate EntityAbout passes to TagComboBox, centralized for the quick-action tag picker.
	const canManageTags = computed<boolean>(() => hasAdminRole.value);

	// LSP-21829: gates the Reviews page's "Review Settings" button — the same role set as the
	// settings-google-review-settings route, so a User never sees a link the route guard would refuse.
	const canManageGoogleReviewSettings = computed<boolean>(() => hasAdminRole.value);

	onMounted(async () => {
		try {
			const response = ((await api.getMe()) as MeAccountResponse | null) ?? null;
			me.value = response;
			if (!response?.account?.roles?.[0]?.name) {
				// Silent short-circuit: gates collapse to false with no signal otherwise.
				// `logInfo` is the right channel here — `datadogLogs.logger.info(...)` is
				// called directly, NOT gated by the `forwardConsoleLogs: ["error"]` filter
				// (that filter only governs the console-mirror channel). Using `logError`
				// here would fabricate a synthetic `Error("null")` and pollute the
				// `status:error` triage queue for an informational breadcrumb.
				logInfo("useCurrentUserRole: /me returned no role; admin CTAs will be hidden", {
					composable: "useCurrentUserRole",
					hasAccount: Boolean(response?.account),
					rolesLength: response?.account?.roles?.length ?? 0
				});
			}
		} catch (error) {
			logError("useCurrentUserRole: failed to fetch current user; admin CTAs will be hidden", error, {
				composable: "useCurrentUserRole"
			});
		} finally {
			isLoading.value = false;
		}
	});

	return {
		me,
		roleName,
		canManageSparks,
		canManageUsers,
		canManageGuidedInstructions,
		canManageTags,
		canManageGoogleReviewSettings,
		isLoading
	};
}
